/* eslint-disable react-refresh/only-export-components */
// 本文件额外 export collectRewriteHints(纯函数,供单测直接引用)——同 shadcn/ui
// badge.tsx/button.tsx 的有意模式,react-refresh 仅关注 HMR。
/**
 * 官媒舱文章工作台(R2-T5,照 dept-reporter-workbench.html):
 * 左 = 标题三选 / 摘要 / 正文段落卡(段落级重写);右 rail 三 tab = 质量报告 / 版本 / 配图。
 *
 * ═══ 契约要点 ═════════════════════════════════════════════════════════════════
 * · 标题三选走 POST /articles/{id}/select-title(只许 AI 候选内选;自由改标题=改内容走重写链)
 * · 段落重写 = startTask(REWRITE_SECTION{sectionId,sectionContent,rewriteInstruction}),
 *   全文重写 = REWRITE_FULL —— 两者都是生成型:草稿 CAS 进 GENERATING,重写后自动重过守卫+评分
 * · 版本回滚 = POST /articles/{id}/rollback/{versionNo},回滚本身落新版本(历史不可变)
 * · 配图走视觉舱 FREEFORM_RAW 单图任务(见 model/reporterImages.ts;插入排版细节留白天 TODO)
 */
import { maskVendor } from "@/shared/lib/vendorMask";
import { useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import {
  Check,
  Eye,
  History,
  ImageIcon,
  Loader2,
  Newspaper,
  Pencil,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";

import { Button } from "@/shared/ui/button";
import { PageHeader } from "@/shared/ui/PageHeader";
import { StepIndicator } from "@/shared/ui/StepIndicator";
import { Textarea } from "@/shared/ui/textarea";
import { CenteredLoader } from "@/shared/ui/QueryState";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { useMutationWithToast } from "@/shared/hooks/useMutationWithToast";
import { useCompliance } from "@/contexts/AuthContext";
import { useConfirm } from "@/shared/hooks/use-confirm";
import { toast } from "@/shared/hooks/use-toast";
import { IMAGE_ACCEPT } from "@/shared/lib/uploadImageFormats";
import { cn } from "@/shared/lib/utils";
import { SKU, imageVariant } from "@/entities/pricing/model/priceBook";
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import {
  REPORTER_MAY_EXCEED_NOTE,
  REPORTER_REWRITE_NOTE,
  reporterArticleCreditsApprox,
} from "@/entities/scene-card/model/creditRates";
import { acquireQuoteId } from "@/entities/pricing/model/useQuote";
import { posterService } from "@/features/poster/api/posterService";
import { reporterService } from "@/features/reporter/api/reporterService";
import { lastFailedGenTask, rewriteFailureText } from "@/features/reporter/model/rewriteFailure";
import {
  GATE_LABEL,
  GENRE_LABEL,
  humanizeReporterText,
  severityLabel,
} from "@/features/reporter/model/labels";
import {
  parseDimScores,
  parseSections,
  parseStringList,
  parseTitles,
  parseWarnings,
  parseImageSuggestions,
  type ArticleSection,
  type ImageSuggestion,
} from "@/features/reporter/model/parse";
import {
  countWords,
  imagePromptOf,
  imageSlotsOf,
  matchTasksToSlots,
  REPORTER_IMAGE_COUNT,
  REPORTER_IMAGE_TIER,
  reporterImageProvider,
  reporterSlotQuoteParams,
  reporterTaskName,
  type ReporterImageSlot,
} from "@/features/reporter/model/reporterImages";

const STEPS = ["素材", "规划", "成稿"] as const;

/**
 * 四维(ai quality_score.WEIGHTS 同源)。1004 T10:原标签带权重「×.40」,用户读不懂,去掉;
 * 维度顺序仍按权重从高到低排,权重本身只在 ai 侧。
 */
const DIMS: { key: string; label: string }[] = [
  { key: "factBoundary", label: "事实边界" },
  { key: "genreFit", label: "体裁适配" },
  { key: "deliverability", label: "可交付性" },
  { key: "warningsQuality", label: "提醒质量" },
];

const GATE_META: Record<string, { label: string; cls: string }> = {
  // 1004 T10:可见文字只放中文,码留在 data-reporter-gate 上
  PASS: { label: GATE_LABEL.PASS, cls: "bg-success/15 text-success" },
  WARNING: { label: GATE_LABEL.WARNING, cls: "bg-warning/15 text-warning" },
  BLOCKED: { label: GATE_LABEL.BLOCKED, cls: "bg-destructive/15 text-destructive" },
};

const SEVERITY_CLS: Record<string, string> = {
  HIGH: "bg-destructive/15 text-destructive",
  MEDIUM: "bg-warning/15 text-warning",
  INFO: "bg-muted text-muted-foreground",
};

/**
 * 重写建议芯片池(两个重写弹窗共用):质检 suggestionsJson + 结构化提醒里的 suggestion 字段
 * 合并成「点一下就填进指令框」的候选,用户不用自己想「怎么改能提分」。
 * suggestionsJson 解析复用 parseStringList(同一条容错口径,坏 JSON/非数组按空处理,不抛错;
 * 不在这里另起一份 JSON.parse ——见文件头 parse.ts 的规矩)。
 * 去重按 trim 后全等,剔除空串,保序(quality 建议在前、warnings 在后),封顶 4 条。
 */
export function collectRewriteHints(
  suggestionsJson: string | null | undefined,
  warnings: { suggestion?: string }[]
): string[] {
  const fromQuality = parseStringList(suggestionsJson);
  const fromWarnings = warnings.map((w) => w.suggestion ?? "");
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of [...fromQuality, ...fromWarnings]) {
    const t = humanizeReporterText(raw);
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
    if (out.length >= 4) break;
  }
  return out;
}

/** 建议芯片行(段落重写/全文重写两个弹窗共用);hints 为空整行不渲染(不留空壳)。 */
function RewriteHintChips({ hints, onPick }: { hints: string[]; onPick: (hint: string) => void }) {
  if (hints.length === 0) return null;
  return (
    <div className="space-y-1.5">
      <p className="text-2xs text-muted-foreground">质检建议(点选填入)</p>
      <div className="flex flex-wrap gap-1.5">
        {hints.map((hint) => (
          <button
            key={hint}
            type="button"
            title={hint}
            onClick={() => onPick(hint)}
            data-reporter-rewrite-hint=""
            className="max-w-[240px] truncate rounded-full border border-border px-2.5 py-1 text-2xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground"
          >
            {hint}
          </button>
        ))}
      </div>
    </div>
  );
}

type RailTab = "quality" | "versions" | "images";

export default function ReporterWorkbenchPage() {
  // 视觉舱 1K 档单价来自后端价目表(官媒配图走的就是海报那条线)
  const { book: priceBook } = usePriceBook();
  // 1011 domesticOnly:配图渠道换国内(建单 / 报价 / 单价同一个值)
  const { domesticOnly } = useCompliance();
  const imageProvider = reporterImageProvider(domesticOnly);
  const image1kCredits = priceBook.unit(
    SKU.posterImage,
    imageVariant(imageProvider, REPORTER_IMAGE_TIER)
  );
  const navigate = useNavigate();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const draftId = Number(id);

  const draftQ = useQuery({
    queryKey: ["reporter-draft", draftId],
    queryFn: () => reporterService.getDraft(draftId),
    enabled: Number.isFinite(draftId),
    refetchInterval: (q) => {
      const d = q.state.data;
      if (!d) return false;
      return d.status === "GENERATING" || d.recentTasks.some((t) => t.status === "RUNNING")
        ? 2500
        : false;
    },
  });
  const draft = draftQ.data;
  const article = draft?.article ?? null;
  const quality = draft?.quality ?? null;
  const generating = draft?.status === "GENERATING";
  // N3(1013):重写任务在 ai 侧失败(余额耗尽 / 超时…)时草稿翻 FAILED,以前工作台一声不吭。
  const failedGenTask = draft?.status === "FAILED" ? lastFailedGenTask(draft.recentTasks) : null;

  const titles = useMemo(() => parseTitles(article?.titlesJson), [article?.titlesJson]);
  const sections = useMemo(() => parseSections(article?.sectionsJson), [article?.sectionsJson]);
  const warnings = useMemo(() => parseWarnings(article?.warningsJson), [article?.warningsJson]);
  const dimScores = useMemo(() => parseDimScores(quality?.dimScoresJson), [quality?.dimScoresJson]);
  const failedRules = useMemo(
    () => parseStringList(quality?.failedRulesJson),
    [quality?.failedRulesJson]
  );
  const suggestions = useMemo(
    () => parseImageSuggestions(article?.imageSuggestionsJson),
    [article?.imageSuggestionsJson]
  );
  const slots = useMemo(() => imageSlotsOf(suggestions), [suggestions]);
  const totalWords = useMemo(
    () => sections.reduce((n, s) => n + countWords(s.content), 0),
    [sections]
  );
  // 段落重写/全文重写两个弹窗共用同一份建议芯片(质检结论就是「怎么改能提分」的现成答案)。
  const rewriteHints = useMemo(
    () => collectRewriteHints(quality?.suggestionsJson, warnings),
    [quality?.suggestionsJson, warnings]
  );

  const [tab, setTab] = useState<RailTab>("quality");
  // S2:摘要重写共用段落重写弹窗,summary=true 走 target:"summary" 白名单口(不造假按钮)
  const [rewriteTarget, setRewriteTarget] = useState<{
    id: string;
    heading: string;
    summary?: boolean;
  } | null>(null);
  const [fullRewriteOpen, setFullRewriteOpen] = useState(false);
  const [instruction, setInstruction] = useState("");
  // 建议芯片点选:已有内容就换行追加,空则直接填入。
  const applyHint = (hint: string) =>
    setInstruction((prev) => (prev ? prev.replace(/\n?$/, "\n") + hint : hint));
  // 批二 P4 手改态:段落内联编辑 / 摘要内联编辑 / 自由标题输入
  const [editSection, setEditSection] = useState<ArticleSection | null>(null);
  const [editSummary, setEditSummary] = useState<string | null>(null);
  const [customTitle, setCustomTitle] = useState("");
  const uploadSlotRef = useRef<string>("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  /* ── 版本(切到版本 tab 才拉) ── */
  const versionsQ = useQuery({
    queryKey: ["reporter-versions", article?.id],
    queryFn: () => reporterService.listVersions(article!.id),
    enabled: !!article && tab === "versions",
  });

  /* ── 配图任务回捞(S2:归属列优先+名兜底,服务端一口;有 RUNNING 槽位就轮询) ── */
  const imageTasksQ = useQuery({
    queryKey: ["reporter-images", draftId],
    queryFn: async () => {
      const page = await posterService.listTasks({
        page: 0,
        size: 20,
        reporterDraftId: draftId,
      });
      return page.records ?? [];
    },
    enabled: Number.isFinite(draftId) && slots.length > 0,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((t) => t.status === "RUNNING" || t.status === "PENDING")
        ? 3000
        : false,
  });
  const slotTasks = useMemo(
    () => matchTasksToSlots(imageTasksQ.data ?? [], draftId),
    [imageTasksQ.data, draftId]
  );

  /* 批二 P3 正文插排:段落 id → 锚在该段的已成图(工作台预览与导出同口径)。 */
  const sectionImages = useMemo(() => {
    const out: Record<string, { slot: string; label: string; thumb: string }[]> = {};
    for (const s of slots) {
      if (s.slot === "cover" || !s.suggestion.sectionId) continue;
      const t = slotTasks[s.slot];
      if (!t?.coverThumbUrl) continue;
      (out[s.suggestion.sectionId] ??= []).push({
        slot: s.slot,
        label: s.label,
        thumb: t.coverThumbUrl,
      });
    }
    return out;
  }, [slots, slotTasks]);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["reporter-draft", draftId] });

  const selectTitleMut = useMutationWithToast({
    mutationFn: (title: string) => reporterService.selectTitle(article!.id, title),
    errorToast: "标题没选上",
    onSuccess: () => invalidate(),
  });

  const rewriteMut = useMutationWithToast({
    mutationFn: (p: {
      type: "REWRITE_FULL" | "REWRITE_SECTION";
      sectionId?: string;
      sectionContent?: string;
      rewriteInstruction: string;
      target?: "summary";
    }) => reporterService.startTask(draftId, p),
    errorToast: "重写没发起来",
    onSuccess: () => {
      setRewriteTarget(null);
      setFullRewriteOpen(false);
      setInstruction("");
      invalidate();
    },
  });

  const rollbackMut = useMutationWithToast({
    mutationFn: (versionNo: number) => reporterService.rollback(article!.id, versionNo),
    successToast: "已回滚(回滚本身也是一个新版本)",
    errorToast: "回滚失败",
    onSuccess: () => {
      invalidate();
      qc.invalidateQueries({ queryKey: ["reporter-versions", article?.id] });
    },
  });

  const createSlotTask = async (slot: ReporterImageSlot) => {
    // ★修复波 0926(代理 H):配图是真出图真扣钱,提交前落一张正式报价单随建单请求走。
    //   取单失败返 null 照常提交(后端软着陆 WARN),不把配图堵死。
    const quoteId = await acquireQuoteId(reporterSlotQuoteParams(imageProvider));
    return posterService.createTask({
      ...(quoteId ? { quoteId } : {}),
      name: reporterTaskName(draftId, slot.slot),
      // S2 正解:归属列(V18)承载文章↔配图关联;任务名约定保留做人读展示+存量兜底
      reporterDraftId: draftId,
      reporterSlot: slot.slot,
      renderMode: "SERVER_OVERLAY",
      provider: imageProvider,
      ratio: slot.ratio,
      resolutionTier: REPORTER_IMAGE_TIER,
      requestedCount: REPORTER_IMAGE_COUNT,
      content: {
        sourceType: "FREEFORM_RAW",
        productName: "官媒配图",
        sellingPoints: [],
        description: "",
        rawPrompt: imagePromptOf(slot.suggestion),
      },
      brand: { qrCodeMode: "NONE" },
    });
  };

  const genImageMut = useMutationWithToast({
    mutationFn: (slotKey: string) => createSlotTask(slots.find((s) => s.slot === slotKey)!),
    successToast: "配图任务已发起(计费走视觉舱)",
    errorToast: "配图没发起来",
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reporter-images", draftId] }),
  });

  /* ── 批二 P2 一键批量配图:串行提交;单张失败不否决其余;余额不足即收手并明说 ── */
  const pendingSlots = useMemo(() => slots.filter((s) => !slotTasks[s.slot]), [slots, slotTasks]);
  const batchImagesMut = useMutationWithToast({
    mutationFn: async () => {
      let sent = 0;
      const failed: string[] = [];
      let stoppedForCredits = false;
      for (const slot of pendingSlots) {
        try {
          await createSlotTask(slot);
          sent += 1;
        } catch (e) {
          const msg = e instanceof Error ? e.message : String(e);
          if (/积分|余额|quota|credit/i.test(msg)) {
            // 共享前提(有钱)没了:后续图位不再提交,明说“未提交未扣费”。
            stoppedForCredits = true;
            break;
          }
          failed.push(`${slot.label}:${msg}`);
        }
      }
      return { sent, failed, stoppedForCredits, total: pendingSlots.length };
    },
    errorToast: "批量配图没发起来",
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["reporter-images", draftId] });
      if (r.stoppedForCredits) {
        toast({
          variant: "destructive",
          title: `积分不足:已发起 ${r.sent}/${r.total} 张后收手`,
          description: "剩余图位未提交、未扣费;充值后可回配图 tab 逐张补。",
        });
      } else if (r.failed.length) {
        toast({
          variant: "destructive",
          title: `已发起 ${r.sent}/${r.total} 张,${r.failed.length} 张失败`,
          description: r.failed.slice(0, 3).join(";"),
        });
      } else {
        toast({ title: `已发起 ${r.sent} 张配图任务(计费走视觉舱)` });
      }
    },
  });

  /* ── 批二 P4 手改保存(整包;不重跑评分,评分对应 AI 版本) ── */
  const manualSaveMut = useMutationWithToast({
    mutationFn: (patch: {
      selectedTitle?: string;
      summary?: string;
      sections?: ArticleSection[];
      imageSuggestions?: ImageSuggestion[];
    }) =>
      reporterService.manualSave(article!.id, {
        selectedTitle: patch.selectedTitle ?? null,
        summary: patch.summary ?? null,
        sectionsJson: JSON.stringify(patch.sections ?? sections),
        tagsJson: null,
        imageSuggestionsJson: patch.imageSuggestions
          ? JSON.stringify(patch.imageSuggestions)
          : null,
      }),
    successToast: "已保存(记为手改版本;质量评分对应上一个 AI 版本)",
    errorToast: "保存失败",
    onSuccess: () => {
      setEditSection(null);
      setEditSummary(null);
      setCustomTitle("");
      invalidate();
      qc.invalidateQueries({ queryKey: ["reporter-versions", article?.id] });
    },
  });

  /* ── 批二 P3 图位上传/移除 ── */
  const uploadImageMut = useMutationWithToast({
    mutationFn: ({ slot, file }: { slot: string; file: File }) =>
      reporterService.uploadSlotImage(draftId, slot, file),
    successToast: "已上传并挂到图位(上传不扣积分)",
    errorToast: "上传失败",
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reporter-images", draftId] }),
  });

  const removeImageMut = useMutationWithToast({
    mutationFn: (slot: string) => reporterService.removeSlotImage(draftId, slot),
    successToast: "已从图位移除(作品本体还在视觉舱作品线)",
    errorToast: "移除失败",
    onSuccess: () => qc.invalidateQueries({ queryKey: ["reporter-images", draftId] }),
  });

  /* ══════════ 渲染 ══════════ */

  if (draftQ.isLoading) return <CenteredLoader />;

  if (!draft || !article) {
    return (
      <div className="mx-auto max-w-3xl space-y-6">
        <PageHeader icon={Newspaper} title="文章工作台" subtitle="这篇草稿还没有成稿。" />
        <Button
          onClick={() => navigate(draft ? `/ai-jizhe/create?draftId=${draftId}` : "/ai-jizhe")}
        >
          {draft ? "回创建页继续(素材→规划→生成)" : "回新闻舱"}
        </Button>
      </div>
    );
  }

  const gate = quality ? GATE_META[quality.gate ?? ""] : null;
  const currentTitle = article.selectedTitle || titles[0] || "未命名文章";

  return (
    <div className="w-full space-y-5">
      <PageHeader
        backTo="/ai-jizhe"
        icon={Newspaper}
        title={currentTitle}
        subtitle={`${GENRE_LABEL[draft.genre] ?? draft.genre} · v${article.currentVersion} · ${totalWords} 字(目标 ${draft.targetWords ?? "—"}±5%)`}
        actions={
          <div className="flex items-center gap-2">
            <StepIndicator
              steps={STEPS}
              current={3}
              label="新闻舱写作流程"
              domainAttr="data-reporter-steps"
            />
            <Button
              variant="outline"
              disabled={generating}
              onClick={() => setFullRewriteOpen(true)}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              全文重写…
            </Button>
            <Button onClick={() => navigate(`/ai-jizhe/preview/${draftId}`)}>
              <Eye className="mr-2 h-4 w-4" />
              成稿预览 / 交付 →
            </Button>
          </div>
        }
      />

      {quality && gate && (
        <div className="flex items-center gap-2 text-xs">
          <span
            className={cn("rounded-full px-3 py-1 font-bold", gate.cls)}
            data-reporter-gate={quality.gate}
          >
            {gate.label} · {quality.totalScore ?? "—"} 分
          </span>
          {generating && (
            <span className="inline-flex items-center gap-1.5 text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              重写中,完成后自动重过守卫与评分…
            </span>
          )}
        </div>
      )}

      {failedGenTask && (
        <div
          role="alert"
          className="rounded-[calc(var(--radius)+4px)] border border-destructive/40 bg-destructive/10 px-4 py-2.5 text-xs text-destructive"
          data-reporter-rewrite-failed=""
        >
          上次{failedGenTask.type === "GENERATE" ? "生成" : "重写"}没有成功:
          {rewriteFailureText(failedGenTask.errorMessage ?? draft?.errorMessage)}
        </div>
      )}

      {/* 批二 P2:一键批量配图确认条 —— 手动确认才烧积分(不自动),预估先明示 */}
      {pendingSlots.length > 0 && !generating && (
        <div
          className="flex flex-wrap items-center gap-2.5 rounded-[calc(var(--radius)+4px)] border border-primary/40 bg-primary/5 px-4 py-2.5 text-xs"
          data-reporter-batch-images=""
        >
          <ImageIcon className="h-4 w-4 shrink-0 text-primary" />
          <span>
            按规划还差 <b>{pendingSlots.length}</b> 张配图(
            {pendingSlots.map((s) => `${s.label} ${s.ratio}`).join("、")}),预计{" "}
            {image1kCredits == null ? null : <b>≈ {pendingSlots.length * image1kCredits} 积分</b>}
            (视觉舱 1K 档)
          </span>
          <Button
            size="sm"
            className="ml-auto"
            disabled={batchImagesMut.isPending}
            onClick={() => batchImagesMut.mutate(undefined)}
            data-reporter-batch-images-go=""
          >
            {batchImagesMut.isPending ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
            ) : (
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
            )}
            一键配图
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          {/* 标题:AI 候选单选 + 自由标题(批二 P4:自由标题追加进候选再选中,历史不丢) */}
          <section className="rounded-[calc(var(--radius)+4px)] border bg-card p-4">
            <h3 className="mb-2.5 text-sm font-semibold">标题(备选 {titles.length} · 单选)</h3>
            {/**
             * ★选中标识给到「一眼看得出」(2026-08-14 四天反馈校对 TOP#7)。
             *
             * 爱民 0810、琳琳 0811×3 —— 四人五次:「选择标题的选项不明确,颜色很淡,可以在前面
             * 加一个勾选☑️」。原来的选中态是一颗 3.5px 的实心小圆点 + 一圈 25% 透明度的描边,
             * 在浅色卡面上几乎看不出哪条被选中,用户只能靠猜。
             *
             * 现在改成**打勾的方块**(选中 = 主色底 + 白勾),整行同时上主色描边、浅底与「已选」
             * 角标 —— 四处一起变,不指望单一细节扛全部对比度。语义仍是 radio(单选、方向键在组内
             * 走),只是视觉换成勾选:用户说的「加个勾选」要的是看得见,不是换一套交互。
             *
             * ★底色/描边必须画在**里层 span** 上,不能画在 role="radio" 的 button 上:
             *   skin-standard.css:365 有一条
             *     [data-skin="standard"] button[role="radio"] { border/background: … !important }
             *   它是给 Radix 那种小圆钮写的,选中分支认的是 Radix 的 `data-state="checked"`;
             *   手写的 radio 行只有 aria-checked,于是**永远吃未选那一支**,写多少 border-primary /
             *   bg-primary 都被 !important 盖掉 —— 真浏览器里量出来两行底色一模一样(都是白)。
             *   「颜色很淡」的老毛病有一半就在这:代码里明明写了主色,页面上从来没生效过。
             *   单测(jsdom 不加载皮肤 CSS)对这类事一律绿,只有真页面量计算样式才看得见。
             */}
            <div className="flex flex-col gap-2" role="radiogroup" aria-label="标题三选">
              {titles.map((t) => {
                const on = t === (article.selectedTitle || titles[0]);
                return (
                  <button
                    key={t}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    disabled={generating || selectTitleMut.isPending}
                    onClick={() => !on && selectTitleMut.mutate(t)}
                    data-title-option={on ? "on" : "off"}
                    className="block w-full rounded-lg text-left"
                  >
                    <span
                      data-title-row=""
                      className={cn(
                        "flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-[12.5px] leading-relaxed transition-colors",
                        on
                          ? "border-primary bg-primary/10 font-medium text-foreground shadow-[0_0_0_1px_hsl(var(--primary)/.35)]"
                          : "border-border bg-background hover:border-primary/40"
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          "mt-[1px] flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border-2 transition-colors",
                          on ? "border-primary bg-primary text-primary-foreground" : "border-border"
                        )}
                      >
                        {on && <Check className="h-3 w-3" strokeWidth={3} />}
                      </span>
                      {t}
                      {on && (
                        <span className="ml-auto shrink-0 self-center text-2xs font-semibold text-primary">
                          已选
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-2.5 flex items-center gap-1.5">
              <input
                value={customTitle}
                disabled={generating}
                onChange={(e) => setCustomTitle(e.target.value)}
                placeholder="都不满意?手写一个标题"
                className="flex-1 rounded-md border border-border/60 bg-background px-2.5 py-1.5 text-xs"
                aria-label="自定义标题"
                data-reporter-custom-title=""
              />
              <Button
                size="sm"
                variant="outline"
                disabled={generating || !customTitle.trim() || manualSaveMut.isPending}
                onClick={() => manualSaveMut.mutate({ selectedTitle: customTitle.trim() })}
              >
                用这个标题
              </Button>
            </div>
          </section>

          {/* 摘要(S2 重写入口 + 批二 P4 手改) */}
          {article.summary && (
            <section className="rounded-[calc(var(--radius)+4px)] border bg-card p-4">
              <div className="mb-2 flex items-baseline gap-2">
                <h3 className="text-sm font-semibold">摘要</h3>
                <button
                  type="button"
                  disabled={generating || editSummary != null}
                  onClick={() => setEditSummary(article.summary ?? "")}
                  className="ml-auto text-2xs font-semibold text-primary disabled:opacity-50"
                  data-reporter-edit-summary=""
                >
                  编辑
                </button>
                <button
                  type="button"
                  disabled={generating}
                  onClick={() =>
                    setRewriteTarget({ id: "summary", heading: "摘要", summary: true })
                  }
                  className="text-2xs font-semibold text-primary disabled:opacity-50"
                  data-reporter-rewrite-summary=""
                >
                  重写摘要
                </button>
              </div>
              {editSummary != null ? (
                <div className="space-y-2">
                  <Textarea
                    value={editSummary}
                    onChange={(e) => setEditSummary(e.target.value)}
                    className="min-h-[70px] text-[12.5px]"
                  />
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="ghost" onClick={() => setEditSummary(null)}>
                      取消
                    </Button>
                    <Button
                      size="sm"
                      disabled={!editSummary.trim() || manualSaveMut.isPending}
                      onClick={() => manualSaveMut.mutate({ summary: editSummary.trim() })}
                    >
                      保存
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-[12.5px] leading-relaxed text-muted-foreground">
                  {article.summary}
                </p>
              )}
            </section>
          )}

          {/* 正文段落 */}
          <section className="rounded-[calc(var(--radius)+4px)] border bg-card p-4">
            <div className="mb-2.5 flex items-baseline gap-2">
              <h3 className="text-sm font-semibold">正文({sections.length} 段)</h3>
              <span className="text-2xs text-muted-foreground">
                每段可单独重写,重写后自动重过守卫与评分
              </span>
            </div>
            <div className="space-y-2.5">
              {sections.map((s) => (
                <div
                  key={s.id}
                  className="rounded-lg border border-border bg-background px-3.5 py-3"
                  data-reporter-section={s.id}
                >
                  <div className="mb-1.5 flex items-center gap-2">
                    <b className="text-[13px]">{s.heading}</b>
                    <span className="font-mono text-2xs text-muted-foreground">
                      {countWords(s.content)} 字
                    </span>
                    <button
                      type="button"
                      disabled={generating || editSection != null}
                      onClick={() => setEditSection({ ...s })}
                      className="ml-auto inline-flex items-center gap-1 text-2xs font-semibold text-primary disabled:opacity-50"
                      data-reporter-edit-section={s.id}
                    >
                      <Pencil className="h-3 w-3" />
                      编辑本段
                    </button>
                    <button
                      type="button"
                      disabled={generating}
                      onClick={() => setRewriteTarget({ id: s.id, heading: s.heading })}
                      className="text-2xs font-semibold text-primary disabled:opacity-50"
                    >
                      重写本段
                    </button>
                  </div>
                  {editSection?.id === s.id ? (
                    <div className="space-y-2" data-reporter-section-editor="">
                      <input
                        value={editSection.heading}
                        onChange={(e) =>
                          setEditSection({ ...editSection, heading: e.target.value })
                        }
                        className="w-full rounded-md border border-border/60 bg-card px-2.5 py-1.5 text-[12.5px] font-semibold"
                        aria-label="段落小标题"
                      />
                      <Textarea
                        value={editSection.content}
                        onChange={(e) =>
                          setEditSection({ ...editSection, content: e.target.value })
                        }
                        className="min-h-[140px] text-[12.5px] leading-[1.8]"
                      />
                      <div className="flex items-center gap-2">
                        <span className="text-2xs text-muted-foreground">
                          手改直接落版本(不过 AI、不扣积分);质量评分保持对应上一个 AI 版本。
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="ml-auto"
                          onClick={() => setEditSection(null)}
                        >
                          取消
                        </Button>
                        <Button
                          size="sm"
                          disabled={!editSection.content.trim() || manualSaveMut.isPending}
                          onClick={() =>
                            manualSaveMut.mutate({
                              sections: sections.map((x) =>
                                x.id === editSection.id ? editSection : x
                              ),
                            })
                          }
                        >
                          {manualSaveMut.isPending && (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                          )}
                          保存本段
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-[12.5px] leading-[1.8] text-muted-foreground">
                      {s.content}
                    </p>
                  )}
                  {/* 批二 P3:锚在本段的插图直接排在段后(与导出 docx 同位;点击跳配图 tab) */}
                  {(sectionImages[s.id] ?? []).length > 0 && (
                    <div className="mt-2 flex gap-2">
                      {(sectionImages[s.id] ?? []).map((img) => (
                        <button
                          key={img.slot}
                          type="button"
                          title={`${img.label} · 在配图 tab 里换图/移除`}
                          onClick={() => setTab("images")}
                          className="w-28 overflow-hidden rounded-md border border-border"
                        >
                          <img src={img.thumb} alt={img.label} className="w-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* 右 rail:三 tab */}
        <aside className="h-fit overflow-hidden rounded-[calc(var(--radius)+4px)] border bg-card">
          <div className="flex border-b border-border px-2" role="tablist" aria-label="工作台侧栏">
            {(
              [
                { k: "quality", label: "质量报告", icon: ShieldCheck },
                { k: "versions", label: "版本", icon: History },
                { k: "images", label: "配图", icon: ImageIcon },
              ] as const
            ).map((t) => (
              <button
                key={t.k}
                type="button"
                role="tab"
                aria-selected={tab === t.k}
                onClick={() => setTab(t.k)}
                className={cn(
                  "flex items-center gap-1.5 px-3 py-2.5 text-xs transition-colors",
                  tab === t.k
                    ? "border-b-2 border-primary font-semibold text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <t.icon className="h-3.5 w-3.5" />
                {t.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col gap-3.5 p-4">
            {tab === "quality" &&
              (quality ? (
                <>
                  <div className="flex items-center gap-2.5">
                    <span className="text-3xl font-extrabold">{quality.totalScore ?? "—"}</span>
                    {gate && (
                      <span
                        className={cn("rounded-full px-3 py-1 text-2xs font-extrabold", gate.cls)}
                      >
                        {gate.label}
                      </span>
                    )}
                  </div>
                  <div>
                    {DIMS.map((d) => {
                      const v = dimScores[d.key];
                      return (
                        <div
                          key={d.key}
                          className="mb-1.5 grid grid-cols-[96px_1fr_34px] items-center gap-2 text-2xs"
                        >
                          <span>{d.label}</span>
                          <div className="h-[7px] overflow-hidden rounded-full bg-muted">
                            <i
                              className="block h-full bg-primary"
                              style={{ width: `${Math.min(100, v ?? 0)}%` }}
                            />
                          </div>
                          <span className="text-right font-mono text-2xs text-muted-foreground">
                            {v ?? "—"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  {warnings.length > 0 && (
                    <div className="space-y-1.5">
                      {warnings.map((w, i) => (
                        <div
                          key={i}
                          className="rounded-lg border border-border border-l-[3px] border-l-warning bg-background px-2.5 py-2 text-2xs leading-relaxed"
                        >
                          <span
                            className={cn(
                              "mr-1.5 rounded-full px-1.5 py-px text-2xs font-extrabold",
                              SEVERITY_CLS[w.severity]
                            )}
                          >
                            {severityLabel(w.severity)}
                          </span>
                          <b>{humanizeReporterText(w.issue)}</b>
                          {(w.action || w.suggestion) && (
                            <div className="mt-0.5 text-muted-foreground">
                              {w.action && <>处理:{humanizeReporterText(w.action)}</>}
                              {w.suggestion && <> 建议:{humanizeReporterText(w.suggestion)}</>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {failedRules.length > 0 && (
                    <div className="rounded-lg border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-2xs text-destructive">
                      未过规则:{failedRules.map(humanizeReporterText).join("、")}
                    </div>
                  )}
                </>
              ) : (
                <p className="text-xs text-muted-foreground">
                  本版还没有质量评分(生成/重写完成后自动出)。
                </p>
              ))}

            {tab === "versions" && (
              <div>
                {versionsQ.isLoading && <CenteredLoader />}
                {(versionsQ.data ?? []).map((v) => {
                  const isCurrent = v.versionNo === article.currentVersion;
                  return (
                    <div
                      key={v.versionNo}
                      className="flex items-center gap-2.5 border-b border-border py-2 text-2xs last:border-0"
                    >
                      <span className="font-mono font-bold">v{v.versionNo}</span>
                      <span className="rounded-full bg-muted px-1.5 py-px text-2xs text-muted-foreground">
                        {v.sourceType}
                      </span>
                      <span className="text-muted-foreground">
                        {v.createdAt?.slice(0, 16).replace("T", " ")}
                      </span>
                      {isCurrent ? (
                        <span className="ml-auto text-muted-foreground">当前</span>
                      ) : (
                        <button
                          type="button"
                          disabled={generating || rollbackMut.isPending}
                          className="ml-auto font-semibold text-primary disabled:opacity-50"
                          onClick={() =>
                            void confirm({
                              title: `回滚到 v${v.versionNo}?`,
                              description:
                                "回滚会作为一个新版本落档(历史不可变,回滚本身也可回滚)。导出前会按当前版本重新预检。",
                              confirmText: "回滚",
                            }).then((ok) => ok && rollbackMut.mutate(v.versionNo))
                          }
                        >
                          回滚到此版
                        </button>
                      )}
                    </div>
                  );
                })}
                {!versionsQ.isLoading && (versionsQ.data ?? []).length === 0 && (
                  <p className="text-xs text-muted-foreground">还没有版本记录。</p>
                )}
              </div>
            )}

            {tab === "images" && (
              <div className="space-y-3">
                {slots.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    本篇没有配图建议(生成时由 AI 按内容给出)。
                  </p>
                )}
                {slots.map((s) => {
                  const task = slotTasks[s.slot];
                  const running = task && (task.status === "RUNNING" || task.status === "PENDING");
                  return (
                    <div
                      key={s.slot}
                      className="rounded-lg border border-border bg-background p-2.5"
                      data-reporter-image-slot={s.slot}
                    >
                      <div className="mb-1.5 text-2xs leading-relaxed text-muted-foreground">
                        <b className="text-foreground">
                          {s.label}({s.ratio})
                        </b>{" "}
                        · 场景:{s.suggestion.scene}
                        {s.suggestion.style ? ` · 风格:${s.suggestion.style}` : ""}
                      </div>
                      {task?.coverThumbUrl ? (
                        <img
                          src={task.coverThumbUrl}
                          alt={s.label}
                          className="mb-1.5 w-full rounded-md border border-border object-cover"
                        />
                      ) : running ? (
                        <div className="mb-1.5 flex aspect-video items-center justify-center rounded-md border border-dashed border-border text-xs text-muted-foreground">
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          视觉舱生成中…
                        </div>
                      ) : task?.status === "FAILED" ? (
                        <div className="mb-1.5 rounded-md border border-destructive/40 bg-destructive/5 px-2 py-1.5 text-2xs text-destructive">
                          生成失败:{maskVendor(task.errorMessage) || "视觉舱任务失败"}
                        </div>
                      ) : null}
                      <div className="flex gap-1.5">
                        <Button
                          size="sm"
                          variant={task?.coverThumbUrl ? "outline" : "default"}
                          className="flex-1 text-xs"
                          disabled={!!running || genImageMut.isPending}
                          onClick={() => genImageMut.mutate(s.slot)}
                        >
                          {running
                            ? "生成中…"
                            : task?.coverThumbUrl
                              ? "换一张(重新生成)"
                              : "生成本图(视觉舱)"}
                        </Button>
                        {/*
                         * ★0811 学良:「新闻稿的插图最好支持上传图片,AI 出来的图机械感太强」——
                         *   上传**批二 P3 就做了**,他没找到:真机量下来这颗是 **42px 宽的纯图标按钮、
                         *   页面上一个字都没有**,旁边「生成本图(视觉舱)」是 188px 的实心主按钮。
                         *   他看见的是「只能让 AI 生成」,于是提了个已经有的功能。
                         * ★所以修的是**说出自己是什么**,不是加功能:补上文字标签。
                         *   仍保持次级样式(outline)——AI 生成是主路,上传是并列的另一条,不抢主按钮。
                         */}
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1 text-xs"
                          title="上传自己的图(不扣积分)"
                          aria-label={`上传${s.label}`}
                          disabled={!!running || uploadImageMut.isPending}
                          onClick={() => {
                            uploadSlotRef.current = s.slot;
                            fileInputRef.current?.click();
                          }}
                          data-reporter-upload-slot={s.slot}
                        >
                          <Upload className="h-3.5 w-3.5" />
                          传我自己的
                        </Button>
                        {task?.coverThumbUrl && (
                          <Button
                            size="sm"
                            variant="outline"
                            title="从图位移除(作品本体不删)"
                            aria-label={`移除${s.label}`}
                            disabled={removeImageMut.isPending}
                            onClick={() =>
                              void confirm({
                                title: `移除${s.label}的图?`,
                                description:
                                  "只解除与这篇稿的图位关联,作品本体还在视觉舱作品线;之后可重新生成或上传。",
                                confirmText: "移除",
                              }).then((ok) => ok && removeImageMut.mutate(s.slot))
                            }
                            data-reporter-remove-slot={s.slot}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </div>
                      {/* 批二 P3:插图锚可改(封面无锚);改锚=手改保存,预览/导出即时跟着走 */}
                      {s.slot !== "cover" && (
                        <label className="mt-1.5 flex items-center gap-1.5 text-2xs text-muted-foreground">
                          插到段落:
                          <select
                            value={s.suggestion.sectionId}
                            disabled={manualSaveMut.isPending}
                            onChange={(e) => {
                              const idx = slots.findIndex((x) => x.slot === s.slot);
                              manualSaveMut.mutate({
                                imageSuggestions: suggestions.map((sg, i) =>
                                  i === idx ? { ...sg, sectionId: e.target.value } : sg
                                ),
                              });
                            }}
                            className="flex-1 rounded-md border border-border/60 bg-background px-1.5 py-1 text-2xs"
                            data-reporter-anchor-select={s.slot}
                          >
                            <option value="">文末(不锚段落)</option>
                            {sections.map((sec) => (
                              <option key={sec.id} value={sec.id}>
                                {sec.heading || sec.id}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                    </div>
                  );
                })}
                {slots.length > 0 && (
                  <p className="text-2xs leading-relaxed text-muted-foreground">
                    生成计费走视觉舱
                    {image1kCredits == null ? "" : `(1K 档 ${image1kCredits} 积分/张)`}
                    ,上传自己的图不扣;
                    产物同时出现在「我的作品·视觉舱」。封面排顶部,插图排在所锚段落之后。
                  </p>
                )}
              </div>
            )}
          </div>
        </aside>
      </div>

      {/* 批二 P3:图位上传的共享文件输入(槽位在点击时记进 uploadSlotRef) */}
      <input
        ref={fileInputRef}
        type="file"
        accept={IMAGE_ACCEPT}
        className="hidden"
        aria-label="上传配图文件"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file && uploadSlotRef.current) {
            uploadImageMut.mutate({ slot: uploadSlotRef.current, file });
          }
          e.target.value = "";
        }}
      />

      {/* 段落重写弹窗 */}
      <Dialog open={!!rewriteTarget} onOpenChange={(o) => !o && setRewriteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>重写「{rewriteTarget?.heading}」</DialogTitle>
            <DialogDescription>
              只动这一段,其他段不变;重写后自动重过事实守卫与质量评分({REPORTER_REWRITE_NOTE})。
            </DialogDescription>
          </DialogHeader>
          <RewriteHintChips hints={rewriteHints} onPick={applyHint} />
          <Textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="怎么改?例:更口语一点 / 补一句行动指引 / 删掉与产品无关的铺垫"
            className="min-h-[80px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRewriteTarget(null)}>
              取消
            </Button>
            <Button
              disabled={!instruction.trim() || rewriteMut.isPending}
              onClick={() => {
                if (rewriteTarget!.summary) {
                  rewriteMut.mutate({
                    type: "REWRITE_SECTION",
                    target: "summary",
                    sectionContent: article.summary ?? "",
                    rewriteInstruction: instruction.trim(),
                  });
                  return;
                }
                const sec = sections.find((s) => s.id === rewriteTarget!.id);
                rewriteMut.mutate({
                  type: "REWRITE_SECTION",
                  sectionId: rewriteTarget!.id,
                  sectionContent: sec?.content ?? "",
                  rewriteInstruction: instruction.trim(),
                });
              }}
            >
              {rewriteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              发起重写
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 全文重写弹窗 */}
      <Dialog open={fullRewriteOpen} onOpenChange={setFullRewriteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>全文重写</DialogTitle>
            <DialogDescription>
              保持事实边界不变,按你的指令优化整篇表达;完成后自动重过守卫与评分(整篇重写{" "}
              {reporterArticleCreditsApprox(draft.targetWords ?? 0)} 积分,{REPORTER_MAY_EXCEED_NOTE}
              )。
            </DialogDescription>
          </DialogHeader>
          <RewriteHintChips hints={rewriteHints} onPick={applyHint} />
          <Textarea
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            placeholder="整篇怎么改?例:语气更克制 / 压缩到 1000 字 / 开头直接进主题"
            className="min-h-[80px]"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setFullRewriteOpen(false)}>
              取消
            </Button>
            <Button
              disabled={!instruction.trim() || rewriteMut.isPending}
              onClick={() =>
                rewriteMut.mutate({ type: "REWRITE_FULL", rewriteInstruction: instruction.trim() })
              }
            >
              {rewriteMut.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              发起重写
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
